import { locales, localeNames, ValidLocale } from '../i18n/config';
import { useLanguage } from '../i18n/LanguageContext';
import { cn } from '../lib/utils';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe } from 'lucide-react';

export function FloatingLanguageSelector() {
  const { currentLocale, setLanguage } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleChange = (newLocale: ValidLocale) => {
    setLanguage(newLocale);
    setIsExpanded(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "z-50",
        isMobile ? "fixed bottom-4 right-4" : "relative"
      )}
      onMouseEnter={() => !isMobile && setIsExpanded(true)}
      onMouseLeave={() => !isMobile && setIsExpanded(false)}
      onClick={() => isMobile && setIsExpanded(!isExpanded)}
    >
      <div className="relative flex items-center">
        {/* Globe Icon */}
        <motion.div
          animate={{
            scale: isExpanded ? 0.9 : 1,
            opacity: isExpanded ? 0.7 : 1
          }}
          className={cn(
            "bg-background/80 backdrop-blur-sm p-2 rounded-full shadow-lg",
            isMobile ? "absolute right-0" : "absolute -left-10"
          )}
        >
          <Globe className="w-5 h-5 text-foreground/70" />
        </motion.div>

        {/* Language Buttons */}
        <div className={cn(
          "flex gap-2",
          isMobile && "flex-col-reverse items-end"
        )}>
          <AnimatePresence>
            {(isExpanded || currentLocale) && locales.map((loc) => (
              <motion.button
                key={loc}
                onClick={() => handleChange(loc)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200',
                  'backdrop-blur-sm shadow-lg border border-border/5',
                  currentLocale === loc
                    ? 'bg-primary/90 text-primary-foreground scale-110'
                    : isExpanded
                    ? 'bg-background/80 text-foreground/90 hover:bg-background/90'
                    : 'bg-background/50 text-foreground/50',
                  isMobile && 'w-full text-right'
                )}
                initial={isExpanded ? { opacity: 0, scale: 0.8, x: 20 } : false}
                animate={{
                  opacity: isExpanded ? 1 : currentLocale === loc ? 1 : 0,
                  scale: isExpanded ? 1 : currentLocale === loc ? 1 : 0.8,
                  x: 0,
                  y: isMobile && isExpanded ? -10 : 0
                }}
                exit={{ opacity: 0, scale: 0.8, x: 20 }}
                transition={{
                  duration: 0.2,
                  delay: isExpanded ? locales.indexOf(loc) * 0.1 : 0
                }}
                whileHover={{ scale: currentLocale === loc ? 1.1 : 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {localeNames[loc]}
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
